import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Credential|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateHistoryCredential';
export type SPCandidateHistoryCredentialLocNames =
	| `${LocNameStart}LabelSnowflakeBadgeId`
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelStartDate`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelLastExtendedDate`
	| `${LocNameStart}LabelInactiveDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}LabelRetired`
	| `${LocNameStart}LabelProgramNumber`
	| `${LocNameStart}LabelECert`
	| `${LocNameStart}LabelLogo`
	| `${LocNameStart}LabelFilterByStatus`;

export const spCandidateHistoryCredentialLocs: LocDefs<SPCandidateHistoryCredentialLocNames> = {
	SPCandidateHistoryCredentialLabelSnowflakeBadgeId: {
		defaultValue: 'Credential ID',
		description: 'Column heading for: Credential ID.',
		displayPath: `${pathStart}Credential ID`,
		displayType: 'Label',
		feature: HubFeature.SnowflakeDisplayBadgeId,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialTitleTab: {
		defaultValue: 'Candidate Credentials',
		description: 'Tab title',
		displayPath: `${pathStart}Tab title`,
		displayType: 'Title',
		legacyName: 'Credential/Candidates.Credentials.Title.Credentials',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialTitlePage: {
		defaultValue: 'Credentials',
		description: 'Page title',
		displayPath: `${pathStart}Page title`,
		displayType: 'Title',
		legacyName: 'Credential/Candidates.Credentials.PageSectionTitle.Credentials',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelName: {
		defaultValue: 'Name',
		description: 'Column heading for: Name',
		displayPath: `${pathStart}Name`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.Name',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelStartDate: {
		defaultValue: 'Start Date',
		description: 'Column heading for: Start Date',
		displayPath: `${pathStart}Start Date`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.StartDate',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelStatus: {
		defaultValue: 'Status',
		description: 'Column heading for: Status',
		displayPath: `${pathStart}Status`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.Status',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelActiveDate: {
		defaultValue: 'Active Date',
		description: 'Column heading for: Active Date',
		displayPath: `${pathStart}Active Date`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.ActiveDate',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelLastExtendedDate: {
		defaultValue: 'Last Extended Date',
		description: 'Column heading for: Last Extended Date',
		displayPath: `${pathStart}Last Extended Date`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.LastExtendDate',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelInactiveDate: {
		defaultValue: 'Inactive Date',
		description: 'Column heading for: Inactive Date',
		displayPath: `${pathStart}Inactive Date`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.InactiveDate',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Column heading for: Expire Date',
		displayPath: `${pathStart}Expire Date`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.ExpireDate',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelRetired: {
		defaultValue: 'Retired',
		description: 'Column heading for: Retired',
		displayPath: `${pathStart}Retired`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.RetireDate',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelProgramNumber: {
		defaultValue: 'Program Number',
		description: 'Column heading for: Program Number',
		displayPath: `${pathStart}Program Number`,
		displayType: 'Label',
		legacyName: 'Credential.ProgramNumber/Candidates.Credentials.ColumnHeading.ProgramNumber',
		feature: HubFeature.CredentialProgramNumber,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelECert: {
		defaultValue: 'eCert',
		description: 'Column heading for: eCert',
		displayPath: `${pathStart}eCert`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.DownloadEcert',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelLogo: {
		defaultValue: 'Logo',
		description: 'Column heading for: Logo',
		displayPath: `${pathStart}Logo`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.ColumnHeading.DownloadLogo',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
	SPCandidateHistoryCredentialLabelFilterByStatus: {
		defaultValue: 'Filter by Status',
		description: 'Label for: Filter by Status',
		displayPath: `${pathStart}Filter by Status`,
		displayType: 'Label',
		legacyName: 'Credential/Candidates.Credentials.FieldLabel.FilterStatus',
		feature: HubFeature.Credential,
		...sharedProperties,
	},
};

export type SPCandidateHistoryCredentialLocType = LocMap<SPCandidateHistoryCredentialLocNames>;
